import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Popover,
} from "@mui/material";
import PDatePicker from "components/PDatePicker";
import { MouseEvent, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectMyUsersName } from "redux/slices/session";
import { COVERSHEET_PROPS, backend, formatDateBackend, formatDateBackendGuaranteed } from "Backend";
import { useSnackbar } from "notistack";
import { AddCaseIntakesCustomPropertiesDocument, DocumentIntakeViewBasic } from "generated/graphql";
import { Document, Page } from "react-pdf";
import { P } from "theme/typography";
import theme from "theme";
import PSelect from "components/PSelect";
import { fullNameFromParts } from "helpers/name-helpers";
import { runGqlMutation } from "gqlClient";

interface PreviewProps {
  cases: {
    claimantName: string;
    id: number;
  }[];
  coversheetDate?: Date;
}

const Preview = ({ cases, coversheetDate }: PreviewProps) => {
  const [caseId, setCaseId] = useState<number | undefined>(cases[0]?.id);
  const pdfInput = useMemo(() => {
    const cd = formatDateBackend(coversheetDate);

    if (caseId && cd) {
      return {
        url: backend.getCoversheetPreviewUrl(caseId, cd),
        withCredentials: true,
      };
    } else {
      return undefined;
    }
  }, [caseId, coversheetDate]);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
        <P variant="lgSemiBold" sx={{ mr: 2 }}>
          Preview
        </P>
        {cases.length !== 1 ? (
          <Box sx={{ width: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Select Case for Preview</InputLabel>
              <PSelect
                label="Select Case for Preview"
                value={caseId}
                onChange={(evt) => {
                  const caseId = evt.target.value as string;
                  setCaseId(caseId ? parseInt(caseId) : undefined);
                }}>
                {cases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.claimantName}
                  </MenuItem>
                ))}
              </PSelect>
            </FormControl>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[100]}`,
          minHeight: 843,
          display: "flex",
          justifyContent: "center",
        }}>
        {pdfInput !== undefined ? (
          <Document file={pdfInput}>
            <Page pageNumber={1} />
          </Document>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <P variant="base">Select a case and coversheet date for a preview of the Coversheet</P>
          </Box>
        )}
      </Box>
    </>
  );
};

interface DocumentPopoverProps {
  data: {
    documentName: string;
    claimantName: string;
  }[];
}

const DocumentPopover = ({ data }: DocumentPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <>
      <Button sx={{ ml: 1 }} variant="text" onClick={handleClick} color="default">
        {data.length} {data.length === 1 ? "Document" : "Documents"}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        <Card sx={{ display: "flex", flexDirection: "column", p: 1, gap: 1 }}>
          {data.map((d) => (
            <P key={d.documentName} variant="sm" sx={{ color: "grey.700" }}>
              {d.documentName} - {d.claimantName}
            </P>
          ))}
        </Card>
      </Popover>
    </>
  );
};

interface Props {
  open: boolean;
  close: () => void;
  intakes: { id: number; caseId: number; claimantName: string; name: string }[];
  previewProps: PreviewProps;
  coversheetInitialDate: Date | undefined;
}

const CoversheetsDialog = ({ open, close, intakes, previewProps, coversheetInitialDate }: Props) => {
  const [approvalDate] = useState<Date>(new Date());
  const [coversheetDate, setCoversheetDate] = useState<Date | undefined>(coversheetInitialDate || new Date());
  const [merged, setMerged] = useState(true);
  const [saving, setSaving] = useState(false);
  const myName = useAppSelector(selectMyUsersName);
  const { enqueueSnackbar } = useSnackbar();
  const saveDisabled = saving || !approvalDate || !coversheetDate;
  const coversheetDateFormatted = formatDateBackend(coversheetDate);

  const approveCoversheets = async () => {
    if (coversheetDateFormatted) {
      const properties = [
        {
          key: COVERSHEET_PROPS.APPROVED_DATE,
          value: formatDateBackendGuaranteed(approvalDate),
        },
        {
          key: COVERSHEET_PROPS.APPROVED_BY,
          value: myName,
        },
        {
          key: COVERSHEET_PROPS.DATE,
          value: coversheetDateFormatted,
        },
      ];
      const caseIdIntakesMap = intakes.reduce((prev, intake) => {
        if (!prev[intake.caseId]) {
          prev[intake.caseId] = [intake.id];
        } else {
          prev[intake.caseId].push(intake.id);
        }
        return prev;
      }, {});
      try {
        setSaving(true);
        await Promise.all(
          Object.keys(caseIdIntakesMap).map((caseId) => {
            runGqlMutation(AddCaseIntakesCustomPropertiesDocument, {
              caseId: parseInt(caseId),
              intakeIds: caseIdIntakesMap[caseId],
              properties,
            });
          })
        );

        await Promise.all(
          Object.keys(caseIdIntakesMap).map((caseId) =>
            backend.triggerCaseCoversheetExport({
              caseId: parseInt(caseId),
              intakeIds: caseIdIntakesMap[caseId],
              coversheetDate: coversheetDateFormatted,
              merged,
              bulkZip: false,
            })
          )
        );
        enqueueSnackbar("Coversheets approved", { variant: "success" });
        close();
      } catch (e) {
        console.error(e);
        enqueueSnackbar(
          "Error generating coversheet. If the problem persists, you may need to Reprocess with Rebuild PDF on the document.",
          { variant: "error" }
        );
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>
        Approve Coversheets{" "}
        <DocumentPopover
          data={intakes.map((i) => ({
            documentName: i.name,
            claimantName: i.claimantName,
          }))}
        />
      </DialogTitle>
      <DialogContent sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <P variant="lgSemiBold" sx={{ my: 2 }}>
            Configure
          </P>
          <FormControl required>
            <FormLabel>Coversheet Date</FormLabel>
            <PDatePicker date={coversheetDate} setDate={setCoversheetDate} />
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={merged} onChange={() => setMerged(!merged)} />}
            label="Merged"
          />
        </Box>
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Preview {...previewProps} coversheetDate={coversheetDate} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="default" onClick={close}>
          Cancel
        </Button>
        <Button color="default" disabled={saveDisabled} onClick={approveCoversheets} variant="contained">
          {saving ? "Approving" : "Approve"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MultiCaseProps {
  open: boolean;
  close: () => void;
  intakes: DocumentIntakeViewBasic[];
  coversheetInitialDate: Date | undefined;
}

const CoversheetsMultiCaseDialog = ({ open, close, intakes, coversheetInitialDate }: MultiCaseProps) => {
  const caseIdIntakesMap = intakes.reduce((prev, intake) => {
    if (!prev[intake.caseId]) {
      prev[intake.caseId] = fullNameFromParts(intake.claimantGivenName, intake.claimantSurname);
    }

    return prev;
  }, {});

  return (
    <CoversheetsDialog
      open={open}
      close={close}
      intakes={intakes.map((i) => ({
        id: i.id,
        caseId: i.caseId,
        name: i.name,
        claimantName: fullNameFromParts(i.claimantGivenName, i.claimantSurname),
      }))}
      previewProps={{
        cases: Object.keys(caseIdIntakesMap).map((caseId) => ({
          id: parseInt(caseId),
          claimantName: caseIdIntakesMap[caseId],
        })),
      }}
      coversheetInitialDate={coversheetInitialDate}
    />
  );
};

export default CoversheetsMultiCaseDialog;
